import { useEffect, useRef } from "react";
import {
  VERTEX360_COPILOT_DRAWER_TRANSITION_MS,
  getVertex360CopilotDrawerWidth,
} from "../constants/vertex360CopilotConstants";

export const VERTEX360_COPILOT_DRAWER_OPEN_BODY_CLASS = "vertex360-copilot-drawer-open";
export const VERTEX360_COPILOT_DRAWER_WIDTH_CSS_VAR = "--copilot-drawer-width";
export const VERTEX360_COPILOT_LIVECHAT_GAP_PX = 16;

/** Above drawer backdrop (100000). */
export const VERTEX360_COPILOT_LIVECHAT_Z_INDEX = 100001;

const LIVECHAT_HOST_SELECTOR = ".o-livechat-root";

/** Odoo embed (shadow) + newer mail hub (light DOM). */
const LIVECHAT_INNER_SELECTORS = [
  ".o-livechat-LivechatButton",
  'button[part="openChatButton"]',
  ".o-mail-ChatHub-bubbles",
  ".o-mail-ChatBubble",
];

const PATCHED_ATTR = "data-copilot-livechat-patched";

let restoreGeneration = 0;

const bumpRestoreGeneration = () => {
  restoreGeneration += 1;
  return restoreGeneration;
};

const readInlinePx = (value) => {
  if (!value || value === "auto") return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const forEachPatchedElement = (callback) => {
  const visitRoot = (root) => {
    root.querySelectorAll(`[${PATCHED_ATTR}]`).forEach(callback);
  };

  visitRoot(document);

  document.querySelectorAll(LIVECHAT_HOST_SELECTOR).forEach((host) => {
    if (host.shadowRoot) {
      visitRoot(host.shadowRoot);
    }
  });
};

const patchLivechatElement = (el, drawerWidth, { hostOnly = false } = {}) => {
  if (!(el instanceof HTMLElement)) return;

  const computed = window.getComputedStyle(el);

  if (!el.hasAttribute(PATCHED_ATTR)) {
    el.setAttribute(PATCHED_ATTR, "1");
    el.dataset.copilotOriginalRight = el.style.right || "";
    el.dataset.copilotOriginalLeft = el.style.left || "";
    el.dataset.copilotOriginalBottom = el.style.bottom || "";
    el.dataset.copilotOriginalZIndex = el.style.zIndex || "";
    el.dataset.copilotOriginalTransform = el.style.transform || "";
    el.dataset.copilotBaseRight = String(
      readInlinePx(el.style.right) ?? readInlinePx(computed.right) ?? VERTEX360_COPILOT_LIVECHAT_GAP_PX
    );
    el.dataset.copilotBaseLeft = String(
      readInlinePx(el.style.left) ?? readInlinePx(computed.left) ?? 0
    );
    el.dataset.copilotUsesLeft = String(
      (readInlinePx(el.style.left) ?? readInlinePx(computed.left) ?? 0) > 0 &&
        computed.left !== "auto"
    );
  }

  el.style.setProperty("z-index", String(VERTEX360_COPILOT_LIVECHAT_Z_INDEX), "important");
  el.style.setProperty(
    "transition",
    `right ${VERTEX360_COPILOT_DRAWER_TRANSITION_MS}ms ease, left ${VERTEX360_COPILOT_DRAWER_TRANSITION_MS}ms ease, transform ${VERTEX360_COPILOT_DRAWER_TRANSITION_MS}ms ease`,
    "important"
  );

  if (hostOnly) {
    return;
  }

  if (computed.position !== "fixed" && computed.position !== "absolute") {
    return;
  }

  const baseLeft = Number.parseFloat(el.dataset.copilotBaseLeft) || 0;
  const baseRight = Number.parseFloat(el.dataset.copilotBaseRight) ?? VERTEX360_COPILOT_LIVECHAT_GAP_PX;
  const usesLeft = el.dataset.copilotUsesLeft === "true";

  if (usesLeft && baseLeft > 0) {
    el.style.setProperty(
      "left",
      `${Math.max(VERTEX360_COPILOT_LIVECHAT_GAP_PX, baseLeft - drawerWidth)}px`,
      "important"
    );
    el.style.setProperty("right", "auto", "important");
    el.style.setProperty("transform", "none", "important");
    return;
  }

  el.style.setProperty("right", `${baseRight + drawerWidth}px`, "important");
  el.style.setProperty("left", "auto", "important");
  el.style.setProperty("transform", "none", "important");
};

const restoreLivechatElement = (el, generation) => {
  if (!(el instanceof HTMLElement) || !el.hasAttribute(PATCHED_ATTR)) return;

  const snapshot = {
    copilotOriginalRight: el.dataset.copilotOriginalRight || "",
    copilotOriginalLeft: el.dataset.copilotOriginalLeft || "",
    copilotOriginalBottom: el.dataset.copilotOriginalBottom || "",
    copilotOriginalZIndex: el.dataset.copilotOriginalZIndex || "",
    copilotOriginalTransform: el.dataset.copilotOriginalTransform || "",
    copilotBaseRight: el.dataset.copilotBaseRight || "",
    copilotBaseLeft: el.dataset.copilotBaseLeft || "",
    copilotUsesLeft: el.dataset.copilotUsesLeft === "true",
    hostOnly: el.classList.contains("o-livechat-root"),
  };

  const baseLeft = Number.parseFloat(snapshot.copilotBaseLeft) || 0;
  const baseRight = Number.parseFloat(snapshot.copilotBaseRight);
  const usesLeft = snapshot.copilotUsesLeft;

  el.style.setProperty(
    "transition",
    `right ${VERTEX360_COPILOT_DRAWER_TRANSITION_MS}ms ease, left ${VERTEX360_COPILOT_DRAWER_TRANSITION_MS}ms ease, transform ${VERTEX360_COPILOT_DRAWER_TRANSITION_MS}ms ease`,
    "important"
  );

  if (!snapshot.hostOnly) {
    if (usesLeft && baseLeft > 0) {
      el.style.setProperty("left", `${baseLeft}px`, "important");
      el.style.setProperty("right", "auto", "important");
    } else if (Number.isFinite(baseRight)) {
      el.style.setProperty("right", `${baseRight}px`, "important");
      el.style.setProperty("left", "auto", "important");
    }
    el.style.setProperty("transform", snapshot.copilotOriginalTransform || "none", "important");
  }

  window.setTimeout(() => {
    if (generation !== restoreGeneration) return;
    finalizeRestoreLivechatElement(el, snapshot);
  }, VERTEX360_COPILOT_DRAWER_TRANSITION_MS + 50);
};

const finalizeRestoreLivechatElement = (el, snapshot) => {
  if (!(el instanceof HTMLElement)) return;

  ["right", "left", "bottom", "transform", "z-index", "transition"].forEach((prop) => {
    el.style.removeProperty(prop);
  });

  const applyOrRemove = (prop, value) => {
    if (value) {
      el.style.setProperty(prop, value);
    } else {
      el.style.removeProperty(prop);
    }
  };

  applyOrRemove("right", snapshot.copilotOriginalRight);
  applyOrRemove("left", snapshot.copilotOriginalLeft);
  applyOrRemove("bottom", snapshot.copilotOriginalBottom);
  applyOrRemove("z-index", snapshot.copilotOriginalZIndex);
  applyOrRemove("transform", snapshot.copilotOriginalTransform);

  el.removeAttribute(PATCHED_ATTR);
  delete el.dataset.copilotOriginalRight;
  delete el.dataset.copilotOriginalLeft;
  delete el.dataset.copilotOriginalBottom;
  delete el.dataset.copilotOriginalZIndex;
  delete el.dataset.copilotOriginalTransform;
  delete el.dataset.copilotBaseRight;
  delete el.dataset.copilotBaseLeft;
  delete el.dataset.copilotUsesLeft;
};

const collectLivechatTargets = () => {
  const hosts = [];
  const inners = [];
  const seen = new Set();

  const addInner = (el) => {
    if (!(el instanceof HTMLElement) || seen.has(el)) return;
    seen.add(el);
    inners.push(el);
  };

  document.querySelectorAll(LIVECHAT_HOST_SELECTOR).forEach((host) => {
    if (!(host instanceof HTMLElement)) return;
    hosts.push(host);

    if (host.shadowRoot) {
      LIVECHAT_INNER_SELECTORS.forEach((selector) => {
        host.shadowRoot.querySelectorAll(selector).forEach(addInner);
      });
    }
  });

  LIVECHAT_INNER_SELECTORS.forEach((selector) => {
    document.querySelectorAll(`${LIVECHAT_HOST_SELECTOR} ${selector}`).forEach(addInner);
  });

  return { hosts, inners };
};

const applyLivechatOffset = (drawerOpen) => {
  if (!drawerOpen) {
    const generation = bumpRestoreGeneration();
    forEachPatchedElement((el) => restoreLivechatElement(el, generation));
    return;
  }

  bumpRestoreGeneration();
  const drawerWidth = getVertex360CopilotDrawerWidth();
  const { hosts, inners } = collectLivechatTargets();

  hosts.forEach((host) => patchLivechatElement(host, drawerWidth, { hostOnly: true }));
  inners.forEach((el) => patchLivechatElement(el, drawerWidth));
};

const applyDrawerOffset = (drawerOpen) => {
  const { body } = document;

  if (drawerOpen) {
    body.classList.add(VERTEX360_COPILOT_DRAWER_OPEN_BODY_CLASS);
    body.style.setProperty(
      VERTEX360_COPILOT_DRAWER_WIDTH_CSS_VAR,
      `${getVertex360CopilotDrawerWidth()}px`
    );
    applyLivechatOffset(true);
    return;
  }

  body.classList.remove(VERTEX360_COPILOT_DRAWER_OPEN_BODY_CLASS);
  body.style.removeProperty(VERTEX360_COPILOT_DRAWER_WIDTH_CSS_VAR);
  applyLivechatOffset(false);
};

const observeLivechatHosts = (onChange, isActive) => {
  const shadowObservers = [];

  const watchHost = (host) => {
    if (!(host instanceof HTMLElement) || host.dataset.copilotShadowObserved) return;
    host.dataset.copilotShadowObserved = "1";

    if (!host.shadowRoot) return;

    const shadowObserver = new MutationObserver(() => {
      if (isActive()) {
        onChange();
      }
    });
    shadowObserver.observe(host.shadowRoot, { childList: true, subtree: true });
    shadowObservers.push(shadowObserver);
  };

  document.querySelectorAll(LIVECHAT_HOST_SELECTOR).forEach(watchHost);

  const bodyObserver = new MutationObserver(() => {
    if (!isActive()) return;
    onChange();
    document.querySelectorAll(LIVECHAT_HOST_SELECTOR).forEach(watchHost);
  });

  bodyObserver.observe(document.body, { childList: true, subtree: true });

  return () => {
    bodyObserver.disconnect();
    shadowObservers.forEach((observer) => observer.disconnect());
    document.querySelectorAll(LIVECHAT_HOST_SELECTOR).forEach((host) => {
      if (host instanceof HTMLElement) {
        delete host.dataset.copilotShadowObserved;
      }
    });
  };
};

/**
 * Shifts the Odoo livechat widget left while the Copilot drawer is open.
 * Odoo embed renders the bubble inside shadow DOM — host-only CSS cannot move it.
 * @param {boolean} drawerOpen
 */
export const useLivechatDrawerOffset = (drawerOpen) => {
  const cleanupObserversRef = useRef(null);
  const drawerOpenRef = useRef(drawerOpen);

  useEffect(() => {
    drawerOpenRef.current = drawerOpen;
    applyDrawerOffset(drawerOpen);

    if (!drawerOpen) {
      cleanupObserversRef.current?.();
      cleanupObserversRef.current = null;
      return undefined;
    }

    const handleResize = () => {
      if (!drawerOpenRef.current) return;
      document.body.style.setProperty(
        VERTEX360_COPILOT_DRAWER_WIDTH_CSS_VAR,
        `${getVertex360CopilotDrawerWidth()}px`
      );
      applyLivechatOffset(true);
    };

    cleanupObserversRef.current = observeLivechatHosts(
      () => applyLivechatOffset(true),
      () => drawerOpenRef.current
    );

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      cleanupObserversRef.current?.();
      cleanupObserversRef.current = null;
      drawerOpenRef.current = false;
      applyDrawerOffset(false);
    };
  }, [drawerOpen]);
};

export const VERTEX360_COPILOT_LIVECHAT_TRANSITION_MS = VERTEX360_COPILOT_DRAWER_TRANSITION_MS;
